import { useState } from "react";

import { Botao, Cores, Corpo, Icone, Modal } from "@componentes-mu/componentes-mu";

import styles from "./styles.module.scss";
import { AttachmentTicketsResponse, Ticket } from "../../../types/tickets/types";
import { FlexRow } from "../../../components/Flex";

export type ModalViewAttachmentsProps = {
    ticket: Ticket;
    isOpen: boolean;
    attachmentFiles: AttachmentTicketsResponse;
    onClose: () => void;
}

export const ModalViewAttachments = ({ ticket, attachmentFiles, isOpen, onClose }: ModalViewAttachmentsProps) => {

    const baseUrl = "http://app.microuniverso.com.br/DOCUMENTOS/EXTRANET/temp/";

    const anexos = attachmentFiles?.anexoTickets || [];
    const [currentIndex, setCurrentIndex] = useState(0);

    const decodeFileName = (name: string) => decodeURIComponent(name);

    if (anexos.length === 0) {
        return (
            <Modal isOpen={isOpen} onClose={onClose}>
                <div className={styles.emptyContainer}>
                    <h2>Anexos do Ticket #{ticket.ticket}</h2>
                    <p>Este ticket não possui anexos.</p>
                </div>
            </Modal>
        );
    }

    const currentFile = anexos[currentIndex];
    const fileUrl = `${baseUrl}${currentFile.nomeArquivo}`;

    const getFileType = (fileName: string) => {
        const ext = fileName.split(".").pop()?.toLowerCase();
        if (!ext) return "other";

        if (
            ["jpg", "jpeg", "png", "gif", "webp", "jfif"].includes(ext)
        )
            return "image";

        if (ext === "pdf") return "pdf";

        if (["txt", "log", "xml", "ocor"].includes(ext))
            return "text";

        if (["mp4", "webm", "ogg"].includes(ext))
            return "video";

        if (["doc", "docx", "xls", "xlsx"].includes(ext))
            return "office";

        if (["zip", "rar", "7z"].includes(ext))
            return "archive";

        return "other";
    };

    const type = getFileType(currentFile.nomeArquivo);

    const next = () =>
        setCurrentIndex((prev) =>
            prev === anexos.length - 1 ? 0 : prev + 1
        );

    const prev = () =>
        setCurrentIndex((prev) =>
            prev === 0 ? anexos.length - 1 : prev - 1
        );

    const renderPreview = () => {
        switch (type) {
            case "image":
                return (
                    <img
                        src={fileUrl}
                        alt={currentFile.nomeArquivo}
                        className={styles.image}
                    />
                );

            case "pdf":
            case "text":
                return (
                    <iframe
                        src={fileUrl}
                        className={styles.iframe}
                        title={currentFile.nomeArquivo}
                    />
                );

            case "video":
                return (
                    <video
                        controls
                        className={styles.video}
                    >
                        <source src={fileUrl} />
                        Seu navegador não suporta vídeo.
                    </video>
                );

            case "office":
                return (
                    <iframe
                        title="office-viewer"
                        className={styles.iframe}
                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                            fileUrl
                        )}`}
                    />
                );

            case "archive":
                return (
                    <div className={styles.noPreview}>
                        <Icone nome="folder" />
                        <p>
                            Arquivo compactado não pode ser visualizado.
                        </p>
                        <p>Faça o download para acessar.</p>
                    </div>
                );

            default:
                return (
                    <div className={styles.noPreview}>
                        <Icone nome="description" />
                        <p>
                            Preview não disponível para este tipo de
                            arquivo.
                        </p>
                    </div>
                );
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2>
                        Anexos do Ticket #{ticket.ticket}
                    </h2>

                    <Botao
                        onClick={onClose}
                        cor={Cores.MensagensDoSistema.VermelhoADMU}
                    >
                        <Icone nome="close" cor="#FDFDFD" />
                    </Botao>
                </div>

                <div className={styles.carousel}>
                    {anexos.length > 1 && (
                        <button
                            onClick={prev}
                            className={`${styles.navButton} ${styles.left}`}
                        >
                            <Icone nome="chevron_left" />
                        </button>
                    )}

                    <div className={styles.preview}>
                        {renderPreview()}
                    </div>

                    {anexos.length > 1 && (
                        <button
                            onClick={next}
                            className={`${styles.navButton} ${styles.right}`}
                        >
                            <Icone nome="chevron_right" />
                        </button>
                    )}

                </div>

                <FlexRow justifyContent="center">
                    <Corpo tipo="body2" cor={Cores.CoresIdentidadeMU.cinzaMu} negrito={500}>
                        {decodeFileName(currentFile.nomeArquivo)}
                    </Corpo>
                </FlexRow>

                <div className={styles.footer}>
                    {anexos.length > 1 && (
                        <div className={styles.counter}>
                            {currentIndex + 1} / {anexos.length}
                        </div>
                    )}

                    <Botao
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px 18px",
                        }}
                        onClick={() =>
                            window.open(fileUrl, "_blank")
                        }
                    >
                        <Icone
                            nome="download"
                            cor={
                                Cores.CoresIdentidadeMU.brancoMu
                            }
                        />
                        <Corpo
                            tipo="button1"
                            cor={
                                Cores.CoresIdentidadeMU.brancoMu
                            }
                            negrito={700}
                        >
                            Baixar
                        </Corpo>
                    </Botao>
                </div>
            </div>
        </Modal>
    );
}